import React, { useEffect, useState } from "react";
import { app, pages } from "@microsoft/teams-js";
import { Spinner, Text, tokens } from "@fluentui/react-components";

/**
 * The 'TabConfig' component is used to display your group tabs
 * user configuration options. Here you will allow the user to
 * make their choices and once they are done you will need to validate
 * their choices and communicate that to Teams to enable the save button.
 */
const TabConfig: React.FC = () => {
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeTeams = async () => {
      try {
        await app.initialize();

        // Register save handler
        pages.config.registerOnSaveHandler((saveEvent) => {
          const baseUrl = window.location.origin;
          pages.config
            .setConfig({
              suggestedDisplayName: "Kanban",
              entityId: "kanban-board",
              contentUrl: baseUrl + "/index.html#/tab",
              websiteUrl: baseUrl + "/index.html#/tab",
            })
            .then(() => {
              saveEvent.notifySuccess();
            })
            .catch((err) => {
              saveEvent.notifyFailure(err?.message || "Failed to save configuration");
            });
        });

        // Enable the save button
        pages.config.setValidityState(true);
        setInitialized(true);
      } catch (err) {
        console.error("Failed to initialize Teams SDK:", err);
        setError(err instanceof Error ? err.message : "Failed to initialize");
      }
    };

    initializeTeams();
  }, []);

  if (error) {
    return (
      <div style={{ padding: 20, color: tokens.colorNeutralForeground1 }}>
        <Text size={500} weight="semibold">
          Configuration Error
        </Text>
        <p style={{ color: tokens.colorPaletteRedForeground1 }}>{error}</p>
      </div>
    );
  }

  if (!initialized) {
    return (
      <div style={{ padding: 20, display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
        <Spinner size="medium" />
        <Text>Loading configuration...</Text>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 20px', color: tokens.colorNeutralForeground1 }}>
      <Text size={600} weight="semibold" style={{ display: "block", marginBottom: 8 }}>
        Kanban Project Tracker
      </Text>
      <Text style={{ color: tokens.colorNeutralForeground2, display: "block", marginBottom: 24 }}>
        Add a kanban board to this channel to manage tasks and track progress with your team.
      </Text>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <span style={{ fontSize: 20 }}>📋</span>
          <div>
            <Text weight="semibold" style={{ display: "block" }}>Drag-and-drop task management</Text>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Organize tasks across customizable columns and swimlane rows</Text>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <span style={{ fontSize: 20 }}>👥</span>
          <div>
            <Text weight="semibold" style={{ display: "block" }}>Built for teams</Text>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Assign tasks, add comments, attach files, and get activity feed notifications</Text>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <span style={{ fontSize: 20 }}>📊</span>
          <div>
            <Text weight="semibold" style={{ display: "block" }}>Multiple boards</Text>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Create separate boards for different projects within the same channel</Text>
          </div>
        </div>
      </div>

      <Text size={200} style={{ color: tokens.colorNeutralForeground4, display: "block", marginTop: 24 }}>
        Click Save to add the board. You can start creating tasks immediately.
      </Text>
    </div>
  );
};

export default TabConfig;
